import React, { useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  Typography
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Alert, AlertTitle } from "@material-ui/lab";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import HistoryIcon from "@material-ui/icons/History";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import ReplayIcon from "@material-ui/icons/Replay";
import SearchFeaturePageShell from "components/shell/SearchFeaturePageShell";
import SettingsCard from "components/kea/SettingsCard/SettingsCard";
import { useShellAppContext } from "../shell/context/useShellAppContext";
import { useCatalogSync } from "../home/hooks/useCatalogSync";
import { IndexingStatus } from "../home/operatorState";
import {
  catalogSourcePhaseLabel,
  catalogSourceProgress
} from "../catalog-sync/catalogSourcePhase";
import { useLocalizedMessage } from "../../util/useLocalizedMessage";
import { Links } from "../../containers/AppLayout/menu/linkBuilder";
import ProductCatalogSettings from "./ProductCatalogSettings";
import CatalogFeedAccessSection from "./CatalogFeedAccessSection";
import ContextualPricingSection from "./ContextualPricingSection";
import CatalogUpdatePolicySection from "./CatalogUpdatePolicySection";

const useStyles = makeStyles(theme => ({
  section: {
    marginBottom: theme.spacing(2)
  },
  notice: {
    "& .MuiAlert-action": {
      alignItems: "center"
    }
  },
  statusCard: {
    marginTop: 0,
  },
  statusLayout: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    [theme.breakpoints.down("xs")]: {
      alignItems: "flex-start",
      flexDirection: "column"
    }
  },
  statusMain: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1.5),
    minWidth: 0
  },
  statusIcon: {
    color: theme.palette.text.secondary,
    marginTop: 1
  },
  statusIconReady: {
    color: theme.palette.success?.main || "#2e7d32"
  },
  statusIconError: {
    color: theme.palette.error.main
  },
  statusText: {
    maxWidth: 620
  },
  statusActions: {
    display: "flex",
    alignItems: "center",
    flexShrink: 0,
    gap: theme.spacing(1)
  },
  progress: {
    marginTop: theme.spacing(1.5)
  }
}));

function formatDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString();
}

export default function DataSettingsPage() {
  const classes = useStyles();
  const localizeMessage = useLocalizedMessage();
  const {
    capabilities,
    compatibility,
    routeShopId
  } = useShellAppContext();
  const history = useHistory();
  const {
    status,
    progress,
    progressKnown,
    indexedCount,
    totalCount,
    phase,
    sourceProcessed,
    sourceTotal,
    error: syncError,
    isLoading,
    isStarting,
    isDegraded,
    isStaleQueue,
    restartAllowed,
    lastSuccessAt,
    startFullUpdate,
    markReindexRequired,
    restartStaleQueuedJob
  } = useCatalogSync();
  const [settingsSaved, setSettingsSaved] = useState(false);
  const [settingsActionsContainer, setSettingsActionsContainer] = useState(
    null
  );
  const deferredReindexEnabled =
    compatibility?.catalogUpdates?.deferred ??
    capabilities?.deferredReindexV1 === true;

  const isRunning = status === IndexingStatus.RUNNING || isStarting;
  const canStart = !isLoading && !isRunning && !isDegraded;
  const showRestartStale = isStaleQueue && restartAllowed;
  const source = { phase, sourceProcessed, sourceTotal };
  const sourceLabel = catalogSourcePhaseLabel(source, localizeMessage);
  const sourceProgress = catalogSourceProgress(source);
  const displayProgress = sourceLabel
    ? sourceProgress
    : progressKnown
    ? Math.max(0, Math.min(100, progress || 0))
    : null;

  const statusView = useMemo(() => {
    if (isLoading) {
      return {
        title: localizeMessage("search.page.dataSettings.status.checking"),
        description: ""
      };
    }
    if (isDegraded) {
      return {
        title: localizeMessage(
          "search.page.dataSettings.status.unavailable.title"
        ),
        description: localizeMessage(
          "search.page.dataSettings.status.unavailable.description"
        ),
        kind: "neutral"
      };
    }
    if (isRunning) {
      if (sourceLabel) {
        return { title: sourceLabel, description: "", kind: "running" };
      }
      const count =
        totalCount > 0
          ? localizeMessage("search.page.dataSettings.status.running.count", {
              indexed: indexedCount.toLocaleString(),
              total: totalCount.toLocaleString()
            })
          : progressKnown
          ? localizeMessage("search.page.dataSettings.status.running.percent", {
              percent: Math.max(0, Math.min(100, progress || 0))
            })
          : localizeMessage("search.page.dataSettings.status.running.waiting");
      return {
        title: localizeMessage("search.page.dataSettings.status.running.title"),
        description: count,
        kind: "running"
      };
    }
    if (status === IndexingStatus.READY) {
      return {
        title: localizeMessage("search.page.dataSettings.status.ready.title"),
        description: lastSuccessAt
          ? localizeMessage("search.page.dataSettings.status.ready.updated", {
              date: formatDate(lastSuccessAt)
            })
          : localizeMessage(
              "search.page.dataSettings.status.ready.description"
            ),
        kind: "ready"
      };
    }
    if (status === IndexingStatus.ERROR) {
      return {
        title: localizeMessage("search.page.dataSettings.status.error.title"),
        description:
          syncError ||
          localizeMessage("search.page.dataSettings.status.error.description"),
        kind: "error"
      };
    }
    return {
      title: localizeMessage("search.page.dataSettings.status.idle.title"),
      description: localizeMessage(
        "search.page.dataSettings.status.idle.description"
      ),
      kind: "neutral"
    };
  }, [
    indexedCount,
    isDegraded,
    isLoading,
    isRunning,
    lastSuccessAt,
    localizeMessage,
    progress,
    progressKnown,
    sourceLabel,
    status,
    syncError,
    totalCount
  ]);

  const openHistory = () =>
    history.push(Links.searchCatalogHistory(routeShopId));
  const handleUpdateCatalog = () => {
    startFullUpdate();
    openHistory();
  };

  let StatusIcon = HistoryIcon;
  let statusIconClass = classes.statusIcon;
  if (statusView.kind === "ready") {
    StatusIcon = CheckCircleOutlineIcon;
    statusIconClass = `${classes.statusIcon} ${classes.statusIconReady}`;
  } else if (statusView.kind === "error") {
    StatusIcon = ErrorOutlineIcon;
    statusIconClass = `${classes.statusIcon} ${classes.statusIconError}`;
  }

  return (
    <SearchFeaturePageShell
      pageId="dataSettings"
      description={localizeMessage("search.page.dataSettings.description")}
      actions={
        <Box
          ref={setSettingsActionsContainer}
          display="flex"
          alignItems="center"
          data-testid="data-settings-header-actions"
        />
      }
    >
      <SettingsCard className={classes.statusCard} data-testid="catalog-status-card">
        <Box className={classes.statusLayout}>
          <Box className={classes.statusMain}>
            {isLoading ? (
              <CircularProgress size={22} />
            ) : (
              <StatusIcon className={statusIconClass} />
            )}
            <Box className={classes.statusText}>
              <Typography
                variant="subtitle2"
                data-testid="data-indexing-status"
              >
                {statusView.title}
              </Typography>
              {statusView.description && (
                <Typography variant="body2" color="textSecondary">
                  {statusView.description}
                </Typography>
              )}
            </Box>
          </Box>
          <Box className={classes.statusActions}>
            {showRestartStale && (
              <Button
                variant="outlined"
                color="secondary"
                size="small"
                onClick={restartStaleQueuedJob}
                startIcon={<ReplayIcon />}
                data-testid="data-restart-stale-btn"
              >
                {localizeMessage("search.page.dataSettings.restartUpdate")}
              </Button>
            )}
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={handleUpdateCatalog}
              disabled={!canStart}
              startIcon={<PlayArrowIcon />}
              data-testid="start-catalog-rebuild"
            >
              {localizeMessage("search.page.dataSettings.updateCatalog")}
            </Button>
            <Button
              variant="outlined"
              color="primary"
              size="small"
              onClick={openHistory}
              startIcon={<HistoryIcon />}
              data-testid="open-catalog-history"
            >
              {localizeMessage("search.page.dataSettings.catalogHistory")}
            </Button>
          </Box>
        </Box>
        {isRunning && !isDegraded && (
          <LinearProgress
            className={classes.progress}
            variant={displayProgress !== null ? "determinate" : "indeterminate"}
            value={displayProgress ?? undefined}
            data-testid="catalog-update-progress"
          />
        )}
      </SettingsCard>

      <CatalogUpdatePolicySection />
      {!deferredReindexEnabled && settingsSaved && (
        <Box className={classes.section}>
          <Alert
            severity="warning"
            className={classes.notice}
            action={(
              <Button
                size="small"
                color="primary"
                variant="contained"
                onClick={handleUpdateCatalog}
                disabled={!canStart}
                startIcon={<PlayArrowIcon />}
                data-testid="reindex-now-btn"
              >
                {localizeMessage('search.page.dataSettings.updateCatalog')}
              </Button>
            )}
            data-testid="reindex-required-notice"
          >
            <AlertTitle>
              {localizeMessage('search.page.dataSettings.updateRequired.title')}
            </AlertTitle>
            {localizeMessage('search.page.dataSettings.updateRequired.description')}
          </Alert>
        </Box>
      )}

      <ProductCatalogSettings
        capabilities={capabilities}
        actionsContainer={settingsActionsContainer}
        actionsInHeader
        onSaved={({ requiresReindex } = {}) => {
          if (!requiresReindex) return;
          if (deferredReindexEnabled) {
            markReindexRequired({ source: 'data-settings-save' });
          } else {
            setSettingsSaved(true);
          }
        }}
      />

      <ContextualPricingSection />
      <CatalogFeedAccessSection />
    </SearchFeaturePageShell>
  );
}
